import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Activity, ArrowLeft, Clock, FolderGit2, LayoutDashboard, ListChecks, RefreshCw, ScrollText, Settings2, ShieldCheck, Unplug, Users } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "../../../ui/tabs";
import { Button } from "../../../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import { useToast } from "../../../../hooks/use-toast";
import EmptyStateCard from "../../EmptyStateCard";
import EndpointActionButton from "../../Endpoints/EndpointActionButton";
import { saasApi, saasErrorMessage } from "../shared/saasApi";
import { EventFilter, SaasProvider } from "../shared/saasTypes";
import { accountDisplay, connectionHealth, ConnectionTone, PROVIDERS, RANGE_OPTIONS, RangeKey, toTimeRange } from "../shared/saasFormat";
import { FilterField, ListSkeleton, StatusPill, useSaasTenant } from "../shared/SaasUi";
import ActivityTab from "./ActivityTab";
import EventDetailDialog from "./EventDetailDialog";
import EventsTab from "./EventsTab";
import FindingsTab from "./FindingsTab";
import IdentitiesTab from "./IdentitiesTab";
import IdentityDetailModal from "./IdentityDetailModal";
import OverviewTab from "./OverviewTab";
import ResourceDetailModal from "./ResourceDetailModal";
import ResourcesTab from "./ResourcesTab";
import { WorkspaceContext } from "./workspaceContext";

const TABS = ["overview", "identities", "resources", "activity", "findings", "events"] as const;
type WorkspaceTab = (typeof TABS)[number];
const ALL_ACCOUNTS = "all";
const TONE_RANK: Record<ConnectionTone, number> = { healthy: 0, working: 1, warning: 2, error: 3 };

export default function SaasWorkspace({ provider, onBack }: { provider: SaasProvider; onBack: () => void }) {
    const meta = PROVIDERS[provider];
    const navigate = useNavigate();
    const { toast } = useToast();
    const tenantName = useSaasTenant();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = searchParams.get("tab");
    const [tab, setTab] = useState<WorkspaceTab>(TABS.includes(initialTab as WorkspaceTab) ? (initialTab as WorkspaceTab) : "overview");
    const [account, setAccount] = useState<string>(searchParams.get("connectionId") ?? ALL_ACCOUNTS);
    const [rangeKey, setRangeKey] = useState<RangeKey>("30d");
    const [range, setRange] = useState(() => toTimeRange("30d"));
    const [eventPreset, setEventPreset] = useState<EventFilter>({});
    const [identityTarget, setIdentityTarget] = useState<{ connectionId: number; login: string } | null>(null);
    const [resourceTarget, setResourceTarget] = useState<{ connectionId: number; resourceId: string } | null>(null);
    const [eventTarget, setEventTarget] = useState<number | null>(null);

    useEffect(() => {
        setSearchParams(
            (previous) => {
                const next = new URLSearchParams(previous);
                next.set("provider", provider);
                next.set("tab", tab);
                if (account === ALL_ACCOUNTS) next.delete("connectionId");
                else next.set("connectionId", account);
                ["status", "message", "account"].forEach((key) => next.delete(key));
                return next;
            },
            { replace: true },
        );
    }, [provider, tab, account, setSearchParams]);

    const connectionsQuery = useQuery(["saas-connections", provider, tenantName], () => saasApi.connections(provider, tenantName), {
        enabled: Boolean(tenantName),
        refetchInterval: (data) => ((data ?? []).some((connection) => connectionHealth(connection).tone === "working") ? 15_000 : 60_000),
    });
    const connections = useMemo(() => connectionsQuery.data ?? [], [connectionsQuery.data]);

    useEffect(() => {
        if (account !== ALL_ACCOUNTS && connections.length > 0 && !connections.some((connection) => String(connection.id) === account)) {
            setAccount(ALL_ACCOUNTS);
        }
    }, [account, connections]);

    const scoped = account === ALL_ACCOUNTS ? connections : connections.filter((connection) => String(connection.id) === account);
    const health = scoped.map(connectionHealth).sort((left, right) => TONE_RANK[right.tone] - TONE_RANK[left.tone])[0];

    const openEvents = useCallback((filter: EventFilter) => {
        setIdentityTarget(null);
        setResourceTarget(null);
        setEventPreset({ ...filter });
        setTab("events");
    }, []);

    const context: WorkspaceContext = {
        provider,
        tenantName,
        connectionId: account === ALL_ACCOUNTS ? undefined : Number(account),
        connections,
        range,
        rangeKey,
        openIdentity: (connectionId, login) => {
            setResourceTarget(null);
            setIdentityTarget({ connectionId, login });
        },
        openResource: (connectionId, resourceId) => {
            setIdentityTarget(null);
            setResourceTarget({ connectionId, resourceId });
        },
        openEvent: setEventTarget,
        openEvents,
    };

    const sync = useMutation(() => Promise.all(scoped.map((connection) => saasApi.syncConnection(provider, tenantName, connection.id))), {
        onSuccess: (responses) => {
            const started = responses.filter((response) => response.status === "STARTED").length;
            toast({
                title: started > 0 ? "Sync started" : "Sync already running",
                description: started > 0 ? `${started} account${started === 1 ? "" : "s"} syncing. Data refreshes automatically.` : responses[0]?.message,
            });
            connectionsQuery.refetch();
        },
        onError: (error) => {
            toast({ title: "Unable to start the sync", description: saasErrorMessage(error, "Try again."), variant: "destructive" });
        },
    });

    const changeRange = (key: RangeKey) => {
        setRangeKey(key);
        setRange(toTimeRange(key));
    };

    const refresh = () => {
        setRange(toTimeRange(rangeKey));
        connectionsQuery.refetch();
    };

    const manageConnections = () => navigate(`/tenantSettings?setting=saas-apps&app=${provider}`);

    const tabs: { id: WorkspaceTab; label: string; icon: typeof Users }[] = [
        { id: "overview", label: "Overview", icon: LayoutDashboard },
        { id: "identities", label: "Identities", icon: Users },
        { id: "resources", label: meta.resourceLabel, icon: FolderGit2 },
        { id: "activity", label: "Activity", icon: Activity },
        { id: "findings", label: "Findings", icon: ListChecks },
        { id: "events", label: "Audit log", icon: ScrollText },
    ];

    return (
        <div className="flex-1 space-y-6 p-6">
            <div className="saas-header-card">
                <Button variant="outline" size="icon" className="saas-icon-button flex-shrink-0" onClick={onBack} aria-label="Back to SaaS apps"
                        title="Back to SaaS apps">
                    <ArrowLeft className="w-4 h-4" />
                </Button>
                <Tabs
                    value={tab}
                    onValueChange={(value) => {
                        if (value === "events") setEventPreset({});
                        setTab(value as WorkspaceTab);
                    }}
                    className="flex-1 min-w-0"
                >
                    <TabsList className="cea-tabs resource-request-tabs">
                        {tabs.map((item) => {
                            const Icon = item.icon;
                            return (
                                <TabsTrigger key={item.id} value={item.id} className="cea-tab resource-request-tab" data-testid={`tab-saas-${item.id}`}>
                                    <Icon className="w-4 h-4" />
                                    {item.label}
                                </TabsTrigger>
                            );
                        })}
                    </TabsList>
                </Tabs>
            </div>

            <div className="resource-request-filters">
                <FilterField label="Account" icon={<meta.icon />}>
                    <Select value={account} onValueChange={setAccount}>
                        <SelectTrigger data-testid="select-saas-account">
                            <SelectValue placeholder={`All ${meta.label} accounts`} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL_ACCOUNTS}>All {meta.label} accounts</SelectItem>
                            {connections.map((connection) => (
                                <SelectItem key={connection.id} value={String(connection.id)}>
                                    {accountDisplay(connection)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </FilterField>
                <FilterField label="Time range" icon={<Clock />}>
                    <Select value={rangeKey} onValueChange={(value) => changeRange(value as RangeKey)}>
                        <SelectTrigger data-testid="select-saas-range">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {RANGE_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </FilterField>
                <FilterField label="Status" icon={<ShieldCheck />}>
                    <div className="flex items-center h-[42px]">
                        {health ? <StatusPill tone={health.tone} label={health.label} title={health.detail} /> : <span className="text-sm text-muted-foreground">—</span>}
                    </div>
                </FilterField>
                <div className="resource-request-filter-actions">
                    <Button variant="outline" className="saas-outline-button" onClick={refresh} data-testid="button-saas-refresh">
                        <RefreshCw className={`w-4 h-4 ${connectionsQuery.isFetching ? "animate-spin" : ""}`} />
                        Refresh
                    </Button>
                    <Button variant="outline" className="saas-outline-button" onClick={manageConnections} data-testid="button-saas-manage">
                        <Settings2 className="w-4 h-4" />
                        Connections
                    </Button>
                    <EndpointActionButton onClick={() => sync.mutate()} disabled={scoped.length === 0 || sync.isLoading} data-testid="button-saas-sync">
                        <RefreshCw className={`w-4 h-4 ${sync.isLoading ? "animate-spin" : ""}`} />
                        Sync now
                    </EndpointActionButton>
                </div>
            </div>

            {!tenantName || connectionsQuery.isLoading ? (
                <ListSkeleton rows={5} />
            ) : connections.length === 0 ? (
                <EmptyStateCard
                    icon={Unplug}
                    title={`No ${meta.label} accounts connected`}
                    description={`Connect ${meta.label} from Settings to start monitoring identities, access and audit activity.`}
                    action={<Button onClick={manageConnections}>Connect {meta.label}</Button>}
                    className="cloud-card-surface border-dashed rounded-2xl"
                />
            ) : (
                <>
                    {tab === "overview" && <OverviewTab context={context} onNavigate={(next) => setTab(next)} />}
                    {tab === "identities" && <IdentitiesTab context={context} />}
                    {tab === "resources" && <ResourcesTab context={context} />}
                    {tab === "activity" && <ActivityTab context={context} />}
                    {tab === "findings" && <FindingsTab context={context} />}
                    {tab === "events" && <EventsTab context={context} preset={eventPreset} />}
                </>
            )}

            {identityTarget && (
                <IdentityDetailModal context={context} connectionId={identityTarget.connectionId} login={identityTarget.login}
                                     onClose={() => setIdentityTarget(null)} />
            )}
            {resourceTarget && (
                <ResourceDetailModal context={context} connectionId={resourceTarget.connectionId} resourceId={resourceTarget.resourceId}
                                     onClose={() => setResourceTarget(null)} />
            )}
            {eventTarget !== null && <EventDetailDialog context={context} eventId={eventTarget} onClose={() => setEventTarget(null)} />}
        </div>
    );
}
